import _ from 'lodash';

/**
 * Transform an array collection of objects
 * to object collection of objects.
 *
 * @param arr {Array} The collection to iterate over.
 * @param key {String|Number} Collection object key. Optional.
 * @return {Object}
 */
function arrayToObject(arr, key) {
	if (!key) {
		return { ...arr };
	}
	return arr.reduce((obj, item) => {
		// eslint-disable-next-line no-param-reassign
		obj[item[key]] = item;
		return obj;
	}, {});
}

/**
 * Transform object collection of objects
 * to array collection of objects.
 *
 * @param obj {Object} The collection to iterate over.
 * @return {Array}
 */
function objectToArray(obj) {
	return Object.keys(obj).map(key => obj[key]);
}

/**
 * Move item in array.
 * Usually helpful for DnD.
 *
 * @param arr {Array}
 * @param fromIndex {Number} Movable item start index
 * @param toIndex {Number} Movable item destination index
 */
function arrayMove(arr, fromIndex, toIndex) {
	const element = arr[fromIndex];
	arr.splice(fromIndex, 1);
	arr.splice(toIndex, 0, element);
}

/**
 * Returns true for exact number value.
 * @param num {*}
 * @return {Boolean}
 */
const isNumber = num => _.isNumber(num) && !_.isNaN(num);

/**
 * Prepend protocol to url string
 *
 * @param url {String}
 * @param protocol {String}
 * @return {String}
 */
function prependUrlProtocol(url, protocol = 'http') {
	let _url = url;
	if (!/^https?:\/\//i.test(_url)) {
		_url = `${protocol}://${_url}`;
	}
	return _url;
}

/**
 * @param options {Array}
 * @param options.*.label {*}
 * @param options.*.value {*}
 * @param value {*}
 */
const getOptionLabelByValue = (options, value) =>
	_.get(
		_.find(options, option => option.value === value),
		'label'
	);

const sleep = ms => new Promise(resolve => setTimeout(resolve, ms));

/**
 *
 * @param {String} email
 */
function shortNameByEmail(email) {
	if (typeof email !== 'string') {
		return '';
	}

	const emailSplited = email.split('@')[0];
	const firstLetters = emailSplited.split('.');
	const shortName = firstLetters.length === 1 ? firstLetters[0][0] : `${firstLetters[0][0]}${firstLetters[1][0]}`;

	return shortName;
}

/**
 * Cross-browser trigger focus event on element
 * @param element {Node}
 */
function triggerFocus(element) {
	const eventType = 'onfocusin' in element ? 'focusin' : 'focus';
	const bubbles = 'onfocusin' in element;
	let event;

	if ('createEvent' in document) {
		event = document.createEvent('Event');
		event.initEvent(eventType, bubbles, true);
	} else if ('Event' in window) {
		event = new Event(eventType, { bubbles, cancelable: true });
	}

	element.focus();
	element.dispatchEvent(event);
}

/**
 * @param event {MouseEvent}
 * @param config {Object}
 * @param config.by {String}
 * @param config.withScroll {Boolean}
 * @param config.scrollContainer {Element}
 * @return {{x: number, y: number}}
 */
function getMousePosition(event, config = {}) {
	const _config = {
		by: 'page',
		withScroll: true,
		scrollContainer: document.documentElement,
		...config,
	};

	const mainScrollContainer = _config.scrollContainer;
	const { scrollLeft, scrollTop } = mainScrollContainer;

	const x = event[`${_config.by}X`] + (_config.withScroll ? scrollLeft : 0);
	const y = event[`${_config.by}Y`] + (_config.withScroll ? scrollTop : 0);

	return { x, y };
}

const int = v => parseInt(v || 0, 10);

const convertPxToVw = (value, digits = 2, windowWidth = window.innerWidth) => {
	const _value = parseFloat(value);
	if (Number.isNaN(_value)) return '';
	return `${((_value / windowWidth) * 100).toFixed(digits)}vw`;
};

const convertVwToPx = (value, digits = 2, windowWidth = window.innerWidth) => {
	const _value = parseFloat(value);
	if (Number.isNaN(_value)) return '';
	return +((_value * windowWidth) / 100).toFixed(digits);
};

// Removes all ranges from the selection
function clearSelection() {
	if (window.getSelection) {
		// @ts-expect-error ts-migrate FIXME
		window.getSelection().removeAllRanges();
		// @ts-expect-error ts-migrate FIXME
	} else if (document.selection) {
		// @ts-expect-error ts-migrate FIXME
		document.selection.empty();
	}
}

/**
 * Is command(MacOS) or control (Win) key pressed
 * @param event {SyncEvent<MouseEvent>}
 * @returns {boolean}
 */
const isCMD = event => (window.navigator.platform.match('Mac') ? event.metaKey : event.ctrlKey);

/**
 * @link {https://muffinman.io/blog/react-rerender-in-component-did-mount}
 * @param fn
 */
function defer(fn: Function) {
	requestAnimationFrame(() => {
		requestAnimationFrame(() => {
			fn();
		});
	});
}

export {
	arrayMove,
	arrayToObject,
	objectToArray,
	isNumber,
	prependUrlProtocol,
	getOptionLabelByValue,
	sleep,
	shortNameByEmail,
	triggerFocus,
	getMousePosition,
	int,
	convertPxToVw,
	convertVwToPx,
	clearSelection,
	isCMD,
	defer,
};
